import { Search } from '@mui/icons-material';
import React, { useMemo, useState } from 'react';
import {
  ApsListFilter,
  filterDoctors,
} from '../../detail/doctorInfo/filterDoctors';
import {
  DoctorGroup,
  groupCandidatesByDoctor,
} from '../../detail/doctorInfo/groupCandidatesByDoctor';
import GroupedDoctorCard from '../../detail/doctorInfo/GroupedDoctorCard';
import { sortDoctorGroupsForFilter } from '../../detail/doctorInfo/sortDoctorGroups';
import { ApsCandidate } from '../../detail/doctorInfo/types';
import './DoctorListPanel.scss';
import FilterTabs, { ListView } from './FilterTabs';
import SelectionBar from './SelectionBar';

interface DoctorListPanelProps {
  title: string;
  subtitle?: string;
  candidates: ApsCandidate[];
  isLoading: boolean;
  selectedGroup: DoctorGroup | null;
  onSelect: (group: DoctorGroup | null) => void;
  onContinue: () => void;
  onManualEntry: () => void;
  renderOthersPanel: () => React.ReactNode;
}

const DoctorListPanel = ({
  title,
  subtitle,
  candidates,
  isLoading,
  selectedGroup,
  onSelect,
  onContinue,
  onManualEntry,
  renderOthersPanel,
}: DoctorListPanelProps) => {
  const [activeView, setActiveView] = useState<ListView>('All');
  const [query, setQuery] = useState('');

  const groups = useMemo(
    () => groupCandidatesByDoctor(candidates),
    [candidates],
  );

  const visibleGroups = useMemo(() => {
    if (activeView === 'Others') {
      return [];
    }
    const filtered = filterDoctors(groups, activeView as ApsListFilter, query);
    return sortDoctorGroupsForFilter(filtered, activeView as ApsListFilter);
  }, [groups, activeView, query]);

  const showOthers = activeView === 'Others';

  return (
    <div className="doctor-list-panel">
      <div className="panel-header">
        <div className="panel-title">{title}</div>
        {subtitle && <div className="panel-subtitle">{subtitle}</div>}
      </div>

      <div className="action-bar">
        <div className="search-wrap">
          <Search className="search-icon" fontSize="small" />
          <input
            className="search-input"
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search by name, specialization, or NPI…"
          />
        </div>
        <FilterTabs active={activeView} onChange={setActiveView} />
      </div>

      <SelectionBar
        selectedName={selectedGroup?.name ?? null}
        onClear={() => onSelect(null)}
        onContinue={onContinue}
      />

      {showOthers ? (
        renderOthersPanel()
      ) : (
        <>
          <div className="entry-list">
            {isLoading && (
              <div className="empty-state visible">Loading doctors…</div>
            )}
            {!isLoading && visibleGroups.length === 0 && (
              <div className="empty-state visible">
                No doctors match your search.
              </div>
            )}
            {!isLoading &&
              visibleGroups.map(group => (
                <GroupedDoctorCard
                  key={group.key}
                  group={group}
                  selectable
                  selected={selectedGroup?.key === group.key}
                  onSelect={onSelect}
                />
              ))}
          </div>
          <div className="manual-entry">
            <span className="manual-hint">
              Can&apos;t find the right doctor?
            </span>
            <button
              type="button"
              className="manual-link"
              onClick={onManualEntry}
            >
              Enter doctor details manually
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default DoctorListPanel;
